import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useMediaQuery, useTheme } from '@mui/material';
import { Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';

const menuBoxStyle = {
  position: 'absolute',
  zIndex: 3000,
  background: '#fff',
  border: '1px solid #eee',
  boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
  borderRadius: 8,
  minWidth: 180,
  padding: 0,
  margin: 0,
  listStyle: 'none',
  fontSize: 13,
  color: '#222',
};

const menuItemStyle = {
  padding: '8px 16px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  border: 'none',
  background: 'none',
  width: '100%',
  textAlign: 'left',
  fontSize: 13,
  transition: 'background 0.15s',
  margin: 0,
  gap: 8,
};

const menuItemHoverStyle = {
  background: 'rgba(25, 118, 210, 0.08)',
};

const inputBoxStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 16px',
  width: '100%',
  boxSizing: 'border-box',
};

const inputStyle = {
  flex: 1,
  fontSize: 13,
  padding: '6px 8px',
  border: '1px solid #ccc',
  borderRadius: 4,
  outline: 'none',
};

const buttonStyle = {
  fontSize: 13,
  padding: '6px 12px',
  borderRadius: 4,
  border: 'none',
  background: '#1976d2',
  color: '#fff',
  cursor: 'pointer',
  fontWeight: 500,
};

const ProjectMenuPopover = ({
  projectMenuAnchor,
  setProjectMenuAnchor,
  setProjectMenuIdx,
  handleDeleteProject,
  updateProjectName,
  projectName
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(-1);
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(projectName);
  const [menuPos, setMenuPos] = useState({ left: 0, top: 0 });
  const menuRootRef = useRef(null);
  const inputRef = useRef(null);

  // 메뉴 열기/닫기 및 위치 계산
  useEffect(() => {
    if (projectMenuAnchor) {
      setOpen(true);
      const rect = projectMenuAnchor.getBoundingClientRect();
      
      if (isMobile) {
        // 모바일: 메뉴를 아래로, 화면 오른쪽 끝에서 안전한 거리 확보
        const menuWidth = 180;
        const screenWidth = window.innerWidth;
        const safeLeft = Math.min(rect.left, screenWidth - menuWidth - 16); // 16px 여백
        
        setMenuPos({
          left: safeLeft + window.scrollX,
          top: rect.bottom + window.scrollY + 4,
        });
      } else {
        // 데스크톱: 기존 방식 (오른쪽으로)
        setMenuPos({
          left: rect.right + window.scrollX,
          top: rect.top + window.scrollY,
        });
      }
      
      // 다른 메뉴들 자동 닫기 (DOM에서 직접 찾아서 닫기)
      const historyMenus = document.querySelectorAll('[data-menu-type="history"]');
      historyMenus.forEach(menu => {
        if (menu.style.display !== 'none') {
          menu.style.display = 'none';
        }
      });
    } else {
      setOpen(false);
      setIsEditing(false);
    }
  }, [projectMenuAnchor, isMobile]);

  // 메뉴 외부 클릭 시 닫기
  useEffect(() => {
    if (!open) return;
    
    const handleClickOutside = (e) => {
      if (
        menuRootRef.current &&
        !menuRootRef.current.contains(e.target)
      ) {
        setOpen(false);
        setIsEditing(false);
        setProjectMenuAnchor(null);
        setProjectMenuIdx(-1);
      }
    };
    
    // mousedown과 click 둘 다 감지하여 더 확실하게 처리
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [open, setProjectMenuAnchor, setProjectMenuIdx]);

  // 이름 바꾸기 input 자동 포커스
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isEditing]);

  const handleRename = () => {
    if (newName.trim() && newName !== projectName) {
      updateProjectName(newName.trim());
    }
    setIsEditing(false);
    setProjectMenuAnchor(null);
    setProjectMenuIdx(-1);
    setOpen(false);
  };

  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      ref={menuRootRef}
      data-menu-type="project"
      style={{ ...menuBoxStyle, left: menuPos.left, top: menuPos.top, position: 'absolute', margin: 0, padding: 0 }}
    >
      {isEditing ? (
        <div style={inputBoxStyle}>
          <input
            ref={inputRef}
            style={inputStyle}
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleRename()}
            maxLength={30}
          />
          <button style={buttonStyle} onClick={handleRename}>저장</button>
        </div>
      ) : (
        <>
          <div
            style={{
              ...menuItemStyle,
              color: '#1976d2',
              ...(hoverIdx === 0 ? menuItemHoverStyle : {}),
              margin: 0,
            }}
            onMouseEnter={() => setHoverIdx(0)}
            onMouseLeave={() => setHoverIdx(-1)}
            onClick={() => setIsEditing(true)}
          >
            <EditIcon fontSize="small" sx={{ mr: 1 }} />
            이름 바꾸기
          </div>
          <div
            style={{
              ...menuItemStyle,
              color: 'red',
              ...(hoverIdx === 1 ? menuItemHoverStyle : {}),
              margin: 0,
            }}
            onMouseEnter={() => setHoverIdx(1)}
            onMouseLeave={() => setHoverIdx(-1)}
            onClick={() => {
              handleDeleteProject();
              setOpen(false);
              setProjectMenuAnchor(null);
              setProjectMenuIdx(-1);
            }}
          >
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
            프로젝트 삭제
          </div>
        </>
      )}
    </div>,
    document.body
  );
};

export default ProjectMenuPopover; 